/**
 * Assistant de création : transformations de la fiche à chaque choix et statut des étapes.
 * Fonctions pures, testées ; l'interface est dans components/creation.
 */
import {CLANS_BY_ID} from './data/clans';
import {DISCIPLINE_IDS, POWERS_BY_ID} from './data/disciplines';
import {PREDATORS_BY_ID, getPredatorDisciplines} from './data/predators';
import {MIN_CONVICTIONS, MAX_CONVICTIONS, STARTING_HUMANITY} from './data/creation';
import {
  createAdvantage,
  respectsPowerTiers,
  validateAttributes,
  validateSkills,
  validateSpecialties,
  validateDisciplines,
  validatePowers,
  validateAdvantages,
} from './character';

export const CREATION_STEPS = [
  'concept',
  'clan',
  'predator',
  'attributes',
  'skills',
  'disciplines',
  'advantages',
  'convictions',
  'summary',
];

// Règle de table : création en Néonate, 13e génération, Puissance du Sang 1, 0 XP.
// Un Sang-Clair est forcément plus faible : 14e génération, Puissance du Sang 0.
export const STARTING_PROFILE = {ageCategory: 'neonate', generation: 13, bloodPotency: 1};
export const THIN_BLOOD_PROFILE = {ageCategory: 'childe', generation: 14, bloodPotency: 0};

const baseProfile = (clanId) => (CLANS_BY_ID[clanId]?.thinBlood ? THIN_BLOOD_PROFILE : STARTING_PROFILE);

/** Disciplines dans lesquelles le clan peut répartir ses points de création. */
export const getCreationDisciplines = (clanId) => {
  const clan = CLANS_BY_ID[clanId];
  if (!clan || clan.thinBlood) return [];
  if (clan.anyDisciplines) return DISCIPLINE_IDS.filter(id => id !== 'thinBloodAlchemy');
  return clan.disciplines;
};

/** Points de création d'une discipline (son niveau sans le point du prédateur). */
export const getCreationDots = (sheet, disciplineId) => (
  (sheet.disciplines[disciplineId]?.level || 0) - (sheet.predatorDiscipline === disciplineId ? 1 : 0)
);

/** Garde les pouvoirs compatibles avec le nouveau niveau, et retire les disciplines à 0. */
const normalizeDisciplines = (disciplines) => Object.entries(disciplines).reduce((acc, [id, value]) => {
  if (!value.level) return acc;
  const powers = (value.powers || [])
    .filter(powerId => (POWERS_BY_ID[powerId]?.level || 99) <= value.level)
    .reduce((kept, powerId) => (
      kept.length < value.level && respectsPowerTiers([...kept, powerId].map(id => POWERS_BY_ID[id].level), value.level)
        ? [...kept, powerId]
        : kept
    ), []);
  return {...acc, [id]: {level: value.level, powers}};
}, {});

/** Choix du clan : profil de départ, et remise à zéro des disciplines et du prédateur devenus incompatibles. */
export const applyClan = (sheet, clanId) => {
  if (sheet.clan === clanId) return sheet;
  return {
    ...sheet,
    ...baseProfile(clanId),
    clan: clanId,
    disciplines: {},
    predatorType: null,
    predatorDiscipline: null,
    humanity: STARTING_HUMANITY,
    specialties: sheet.specialties.filter(item => item.source !== 'predator'),
    advantages: sheet.advantages.filter(item => item.source !== 'predator'),
  };
};

/**
 * Choix du type de prédateur, de sa spécialité et de sa discipline.
 * Remplace entièrement les effets d'un prédateur choisi auparavant.
 * @param {{predatorId: String, specialty?: {skill: String, name: String}, discipline?: String}} choice
 */
export const applyPredator = (sheet, {predatorId, specialty, discipline}) => {
  const predator = PREDATORS_BY_ID[predatorId];
  if (!predator) return sheet;
  const profile = baseProfile(sheet.clan);

  // Retire le point de l'ancien prédateur
  let disciplines = {...sheet.disciplines};
  if (sheet.predatorDiscipline && disciplines[sheet.predatorDiscipline]) {
    const previous = disciplines[sheet.predatorDiscipline];
    disciplines[sheet.predatorDiscipline] = {...previous, level: previous.level - 1};
  }
  const allowed = getPredatorDisciplines(predatorId, sheet.clan);
  const predatorDiscipline = allowed.includes(discipline) ? discipline : null;
  if (predatorDiscipline) {
    const current = disciplines[predatorDiscipline] || {level: 0, powers: []};
    disciplines[predatorDiscipline] = {...current, level: current.level + 1};
  }
  disciplines = normalizeDisciplines(disciplines);

  return {
    ...sheet,
    predatorType: predatorId,
    predatorDiscipline,
    disciplines,
    humanity: STARTING_HUMANITY + (predator.humanity || 0),
    bloodPotency: profile.bloodPotency + (predator.bloodPotency || 0),
    specialties: [
      ...sheet.specialties.filter(item => item.source !== 'predator'),
      ...(specialty && specialty.skill ? [{skill: specialty.skill, name: specialty.name || '', source: 'predator'}] : []),
    ],
    advantages: [
      ...sheet.advantages.filter(item => item.source !== 'predator'),
      ...predator.advantages.map(item => createAdvantage({
        kind: item.kind,
        name: item.name.fr,
        level: item.level,
        source: 'predator',
      })),
    ],
  };
};

/** Fixe les points de création d'une discipline (0, 1 ou 2) en conservant le point du prédateur. */
export const setCreationDots = (sheet, disciplineId, dots) => {
  const predatorDot = sheet.predatorDiscipline === disciplineId ? 1 : 0;
  const current = sheet.disciplines[disciplineId] || {level: 0, powers: []};
  return {
    ...sheet,
    disciplines: normalizeDisciplines({
      ...sheet.disciplines,
      [disciplineId]: {...current, level: Math.max(0, dots) + predatorDot},
    }),
  };
};

/** Ce pouvoir peut-il être ajouté sans rendre la répartition par paliers impossible ? */
export const canAddPower = (sheet, disciplineId, powerId) => {
  const discipline = sheet.disciplines[disciplineId];
  const power = POWERS_BY_ID[powerId];
  if (!discipline || !power) return false;
  const powers = discipline.powers || [];
  if (powers.includes(powerId) || powers.length >= discipline.level) return false;
  const levels = [...powers, powerId].map(id => POWERS_BY_ID[id]?.level || 0);
  return respectsPowerTiers(levels, discipline.level);
};

/** Coche / décoche un pouvoir : un par point, en respectant les paliers. */
export const togglePower = (sheet, disciplineId, powerId) => {
  const discipline = sheet.disciplines[disciplineId];
  if (!discipline) return sheet;
  const powers = discipline.powers || [];
  let next;
  if (powers.includes(powerId)) {
    next = powers.filter(item => item !== powerId);
  } else if (canAddPower(sheet, disciplineId, powerId)) {
    next = [...powers, powerId];
  } else {
    return sheet;
  }
  return {...sheet, disciplines: {...sheet.disciplines, [disciplineId]: {...discipline, powers: next}}};
};

const result = (errors) => ({valid: errors.length === 0, errors});

const validateConcept = (name, sheet) => result([
  ...(!name || !name.trim() ? [{reason: 'missingName'}] : []),
  ...(!sheet.concept || !sheet.concept.trim() ? [{reason: 'missingConcept'}] : []),
]);

const validatePredatorStep = (sheet) => {
  if (!sheet.predatorType) return result([{reason: 'missingPredator'}]);
  const errors = [];
  const specialty = sheet.specialties.find(item => item.source === 'predator');
  if (!specialty || !specialty.name.trim()) errors.push({reason: 'missingPredatorSpecialty'});
  if (getPredatorDisciplines(sheet.predatorType, sheet.clan).length > 0 && !sheet.predatorDiscipline) {
    errors.push({reason: 'missingPredatorDiscipline'});
  }
  return result(errors);
};

const validateConvictions = (convictions) => {
  const filled = convictions.filter(item => item.conviction && item.conviction.trim());
  return result([
    ...(filled.length < MIN_CONVICTIONS || filled.length > MAX_CONVICTIONS ? [{reason: 'convictionCount'}] : []),
    ...(filled.some(item => !item.touchstone || !item.touchstone.trim()) ? [{reason: 'missingTouchstone'}] : []),
  ]);
};

const mergeResults = (...results) => result(results.flatMap(item => item.errors));

/**
 * Statut de chaque étape de l'assistant.
 * @returns {Object<String, {valid: Boolean, errors: Object[]}>}
 */
export const getStepStatuses = (name, sheet) => {
  const statuses = {
    concept: validateConcept(name, sheet),
    clan: result(sheet.clan ? [] : [{reason: 'missingClan'}]),
    predator: validatePredatorStep(sheet),
    attributes: validateAttributes(sheet.attributes),
    skills: mergeResults(
      validateSkills(sheet.skills, sheet.skillDistribution),
      validateSpecialties(sheet.skills, sheet.specialties, {withPredator: Boolean(sheet.predatorType)}),
    ),
    disciplines: sheet.clan
      ? mergeResults(validateDisciplines(sheet.disciplines, sheet.clan, sheet.predatorDiscipline), validatePowers(sheet.disciplines))
      : result([{reason: 'missingClan'}]),
    advantages: validateAdvantages(sheet.advantages),
    convictions: validateConvictions(sheet.convictions),
  };
  const complete = Object.values(statuses).every(status => status.valid);
  return {...statuses, summary: result(complete ? [] : [{reason: 'incomplete'}])};
};

export const isCreationComplete = (name, sheet) => getStepStatuses(name, sheet).summary.valid;
